import React from "react"
import { Row, Col, Form, Button, AutoComplete, Input, Spin, Divider, Space, Segmented, Alert, Tag } from "antd"
import { PlusOutlined, CarOutlined } from "@ant-design/icons"
import { modules } from "../../config/pages"
import { commonServices } from "./movementItemCatalogHelpers"

export interface MovementItemSearchSectionProps {
  isEdit: boolean
  selectedItem: boolean
  isServiceItem: boolean
  typeFilterOptions: any[]
  activeTypeFilter: string
  onTypeFilterChange: (val: string) => void
  mod: string
  onSelectCommonService: (serviceName: string) => void
  onClearSelection: () => void
  searchValue: { label: string; option: { uid: string } }
  onChangeSearchValue: (text: any, option: any) => void
  onSelectOption: (text: any, option: any) => void
  onFocusSearch: () => void
  onSearchInput: (event: any) => void
  options: any[]
  loadingSearch: boolean
  disableSearch: boolean
  searchPlaceholder: string
  onOpenCreateItemModal: (type?: string) => void
  onOpenInventoryModal: () => void
}

const MovementItemSearchSection: React.FC<MovementItemSearchSectionProps> = ({
  isEdit,
  selectedItem,
  isServiceItem,
  typeFilterOptions,
  activeTypeFilter,
  onTypeFilterChange,
  mod,
  onSelectCommonService,
  onClearSelection,
  searchValue,
  onChangeSearchValue,
  onSelectOption,
  onFocusSearch,
  onSearchInput,
  options,
  loadingSearch,
  disableSearch,
  searchPlaceholder,
  onOpenCreateItemModal,
  onOpenInventoryModal,
}) => {
  return (
    <>
      {!isEdit && !selectedItem && typeFilterOptions.length > 1 && (
        <Row justify="center" style={{ marginBottom: 14 }}>
          <Col xs={24} sm={{ span: 20, offset: 2 }}>
            <Segmented
              block
              options={typeFilterOptions}
              value={activeTypeFilter}
              onChange={(val) => onTypeFilterChange(String(val))}
            />
          </Col>
        </Row>
      )}

      {!isEdit && !selectedItem && (activeTypeFilter === 'SERVICE' || (mod === modules.RegisterEntry && activeTypeFilter === 'ALL')) && (
        <Row justify="center" style={{ marginBottom: 12 }}>
          <Col xs={24} sm={{ span: 20, offset: 2 }}>
            <div style={{ padding: "8px 12px", background: "#f0f5ff", borderRadius: 8, border: "1px solid #d6e4ff" }}>
              <div style={{ fontSize: 11, color: "#1d39c4", marginBottom: 6, fontWeight: 600 }}>
                ⚡ Servicios frecuentes en compras (clic para seleccionar o crear rápido):
              </div>
              <Space wrap size={[6, 6]}>
                {commonServices.map((srv) => (
                  <Button
                    key={srv.name}
                    size="small"
                    type="dashed"
                    style={{ fontSize: 12, borderRadius: 12 }}
                    onClick={() => onSelectCommonService(srv.name)}
                  >
                    {srv.icon} {srv.name}
                  </Button>
                ))}
              </Space>
            </div>
          </Col>
        </Row>
      )}

      {(selectedItem || isEdit) && isServiceItem && (
        <Row justify="center" style={{ marginBottom: 12 }}>
          <Col xs={24} sm={{ span: 20, offset: 2 }}>
            <Alert
              type="info"
              showIcon
              message="Elemento No Inventariable (Servicio / Costo Directo)"
              description="Este servicio se sumará al total de la compra como gasto o costo asociado, sin generar movimientos de existencias físicas en bodega."
              style={{ borderRadius: 6, fontSize: 12 }}
            />
          </Col>
        </Row>
      )}

      <Row gutter={[10, 10]} align="middle">
        <Col xs={24} sm={{ span: 10, offset: 2 }}>
          <Form.Item
            label={
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <span>{isServiceItem ? "Servicio" : "Elemento de Catálogo"}</span>
                {selectedItem && !isEdit && (
                  <Button
                    type="link"
                    size="small"
                    onClick={onClearSelection}
                    style={{ padding: 0, height: 'auto', fontSize: 12 }}
                  >
                    Cambiar registro
                  </Button>
                )}
              </div>
            }
            name="name"
            rules={[{ required: true, message: 'Seleccione un registro' }]}
          >
            <AutoComplete
              classNames={{ popup: { root: "certain-category-search-dropdown" } }}
              value={searchValue.label}
              onChange={onChangeSearchValue}
              onSelect={onSelectOption}
              onFocus={onFocusSearch}
              options={options}
              notFoundContent={loadingSearch ? <div style={{ padding: 8, textAlign: 'center' }}><Spin size="small" /></div> : "Sin resultados"}
              disabled={disableSearch}
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { minWidth: 380, maxWidth: 600, maxHeight: 380 } } }}
              popupRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px', display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }} size="small">
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onOpenCreateItemModal(activeTypeFilter !== 'ALL' ? activeTypeFilter : undefined)}
                      size="small"
                    >
                      Crear Nuevo Elemento
                    </Button>
                    {mod === modules.RegisterEntry && (
                      <Button
                        type="dashed"
                        style={{ borderColor: '#1890ff', color: '#1890ff' }}
                        icon={<CarOutlined />}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onOpenCreateItemModal('SERVICE')}
                        size="small"
                      >
                        + Crear Servicio (Flete / Mano de obra)
                      </Button>
                    )}
                  </Space>
                </>
              )}
            >
              <Input.Search loading={loadingSearch} size="middle" placeholder={searchPlaceholder} onPressEnter={onSearchInput} onSearch={onSearchInput} />
            </AutoComplete>
          </Form.Item>
        </Col>
        {(selectedItem || isEdit) && !isServiceItem && (
          <Col xs={24} sm={{ span: 10 }} style={{ paddingTop: "22px" }}>
            <Button type="dashed" onClick={onOpenInventoryModal}>Ver existencias</Button>
          </Col>
        )}
        {(selectedItem || isEdit) && isServiceItem && (
          <Col xs={24} sm={{ span: 10 }} style={{ paddingTop: "22px" }}>
            <Tag color="geekblue" style={{ fontSize: 12, padding: '2px 10px' }}>Servicio / Costo</Tag>
          </Col>
        )}
      </Row>
    </>
  )
}

export default MovementItemSearchSection
